/* ═══════════════════════════════════════════════════════════════
   The Tables toolbox (T1.2) — dockable, shared by the Designer and the
   Spec Masters Studio.

   Replaces the floating table context toolbar (TableToolbar.tsx,
   retired): every command that toolbar offered lives here now, in one
   toolbox panel that docks/floats/drops-down like any other palette
   instead of appearing and disappearing over the paper. The right-click
   menu (TableMenuSection.tsx) still offers the same set for the mouse
   users who prefer it — this file adds zero editing logic of its own,
   only UI over the framework's own table commands and designer/
   tableCommands' attr helpers.

   Only "Insert table" makes sense with the caret OUTSIDE a table; every
   other control needs `inTable(editor.state)` and is disabled (with a
   "place the cursor in a table" reason) until it's true — one guard,
   checked once, not scattered per-button.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignHorizontalDistributeCenter,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Ban,
  Columns3,
  Expand,
  ListOrdered,
  PaintBucket,
  PanelTop,
  Rows3,
  Scissors,
  Settings2,
  TableCellsMerge,
  TableCellsSplit,
  Table as TableGlyph,
  Trash2,
} from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { insertTableBlock } from './commands';
import {
  BORDER_PENS,
  BORDER_TARGETS,
  borderPen,
  getBorderPen,
  MAX_TABLE_WIDTH_PCT,
  MIN_TABLE_WIDTH_PCT,
  setBorderPen,
  subscribeBorderPen,
  TABLE_NODE_NAME,
  type BorderTarget,
} from './tables';
import { ShadePicker } from './ShadePicker';
import {
  applyBorder,
  autofitColumns,
  distributeColumns,
  hasCellSelection,
  inTable,
  MAX_CELL_PADDING_PT,
  selectedCellAlign,
  selectedCellVAlign,
  selectedRowMinHeight,
  setAllCellBorders,
  setCellPadding,
  setCellVAlign,
  setRowMinHeight,
  setTableJustify,
  setTableWidth,
  splitTableAtSelection,
  tableStyleAt,
} from './tableCommands';

interface Props {
  editor: Editor | null;
  canEdit: boolean;
}

const NOT_IN_TABLE = 'Place the cursor in a table';

/** The glyph each Borders target draws — Word's own little box pictures. The
 *  four single sides reuse the side glyph the cell-border toggles always
 *  drew (tools.css); the rest are drawn in editor.css. */
const TARGET_GLYPH: Record<BorderTarget, string> = {
  top: 'dsg-side-glyph dsg-side-top',
  bottom: 'dsg-side-glyph dsg-side-bottom',
  left: 'dsg-side-glyph dsg-side-left',
  right: 'dsg-side-glyph dsg-side-right',
  insideH: 'dsg-bdr-glyph is-inside-h',
  insideV: 'dsg-bdr-glyph is-inside-v',
  all: 'dsg-bdr-glyph is-outside is-inside-h is-inside-v',
  outside: 'dsg-bdr-glyph is-outside',
  inside: 'dsg-bdr-glyph is-inside-h is-inside-v',
  none: 'dsg-bdr-glyph',
};

/** The session's border pen, shared with the right-click menu. */
export function useBorderPen() {
  return React.useSyncExternalStore(subscribeBorderPen, getBorderPen, getBorderPen);
}

/** The table node the selection sits in, with its position — the same
 *  depth-walk every other table file (tableCommands, TableMenuSection,
 *  the retired TableToolbar) uses. */
function tableAt(editor: Editor): { pos: number; numbered: boolean } | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === TABLE_NODE_NAME) {
      return { pos: $from.before(d), numbered: !!$from.node(d).attrs.numbered };
    }
  }
  return null;
}

/** Vertical alignment of the content inside a cell — Word's three. */
const VALIGNS: ReadonlyArray<{
  value: 'top' | 'middle' | 'bottom';
  label: string;
  icon: typeof AlignVerticalJustifyStart;
}> = [
  { value: 'top', label: 'Vertical align top', icon: AlignVerticalJustifyStart },
  { value: 'middle', label: 'Vertical align middle', icon: AlignVerticalJustifyCenter },
  { value: 'bottom', label: 'Vertical align bottom', icon: AlignVerticalJustifyEnd },
];

/** Where the table sits across the page — same three Word offers. */
const JUSTIFIES: ReadonlyArray<{
  value: 'left' | 'center' | 'right';
  label: string;
  icon: typeof AlignLeft;
}> = [
  { value: 'left', label: 'Table alignment left', icon: AlignLeft },
  { value: 'center', label: 'Table alignment center', icon: AlignCenter },
  { value: 'right', label: 'Table alignment right', icon: AlignRight },
];

/** The size picker's grid — Word's own idiom. 8×8 is generous headroom over
 *  the 3×3 default without needing a "more" affordance. */
const GRID_MAX = 8;

/** "Insert table" — a size-picker popover (kit Popover: portalled, fixed,
 *  anchored, clamped, mousedown-outside + Esc dismiss, focus returned — the
 *  law every popup in this app follows). Hover or the arrow keys move the
 *  highlighted r×c region from the top-left corner; Enter or a click inserts
 *  that size; Esc closes without inserting. Each cell's clickable box is the
 *  32px hit floor (`--dsg-hit`) even though the square it DRAWS is much
 *  smaller — the padding between the two is the grid's own hit-target
 *  law (DESIGN §7), not a smaller target dressed up to look compliant. */
function InsertTableGridPicker({
  disabled,
  onInsert,
}: {
  disabled: boolean;
  onInsert: (rows: number, cols: number) => void;
}) {
  const [open, setOpen] = React.useState(false);
  // 0-indexed: the highlighted region runs from (0,0) to (active.r, active.c)
  // inclusive, so rows = active.r + 1, cols = active.c + 1.
  const [active, setActive] = React.useState({ r: 2, c: 2 });
  const gridRef = React.useRef<HTMLDivElement>(null);

  // Every re-open starts back at the 3×3 default — a stale highlight from
  // the last insert would be a lie about what a bare click does next time.
  React.useEffect(() => {
    if (open) setActive({ r: 2, c: 2 });
  }, [open]);

  const commit = (r: number, c: number) => {
    onInsert(r + 1, c + 1);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setActive((a) => ({ ...a, c: Math.min(GRID_MAX - 1, a.c + 1) }));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setActive((a) => ({ ...a, c: Math.max(0, a.c - 1) }));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => ({ ...a, r: Math.min(GRID_MAX - 1, a.r + 1) }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => ({ ...a, r: Math.max(0, a.r - 1) }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(active.r, active.c);
    }
    // Esc is NOT handled here — it bubbles to Radix's own onEscapeKeyDown,
    // which closes the popover and returns focus to the trigger.
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn"
          disabled={disabled}
          aria-haspopup="dialog"
          title="Insert a table — pick the size"
        >
          <Icon icon={TableGlyph} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Insert table
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto rounded-lg border border-border bg-surface p-2 shadow-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          gridRef.current?.focus();
        }}
      >
        <div
          ref={gridRef}
          tabIndex={0}
          role="grid"
          aria-label="Insert table — choose rows and columns"
          className="dsg-tbl-gridpick"
          onKeyDown={onKeyDown}
        >
          {Array.from({ length: GRID_MAX }, (_, r) => (
            <div key={r} role="row" className="dsg-tbl-gridpick-row">
              {Array.from({ length: GRID_MAX }, (_, c) => {
                const on = r <= active.r && c <= active.c;
                return (
                  <button
                    key={c}
                    type="button"
                    role="gridcell"
                    tabIndex={-1}
                    className={`dsg-tbl-gridpick-cell${on ? ' is-on' : ''}`}
                    aria-label={`${r + 1} by ${c + 1} table`}
                    onMouseEnter={() => setActive({ r, c })}
                    onClick={() => commit(r, c)}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div className="dsg-tbl-gridpick-readout" aria-live="polite">
          {active.r + 1} × {active.c + 1}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TablesPalette({ editor, canEdit }: Props) {
  // Re-render on selection/transaction — the same subscription every other
  // editor-state-reading palette uses (BlocksPalette, the retired
  // TableToolbar).
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', tick);
    editor.on('selectionUpdate', tick);
    return () => {
      editor.off('transaction', tick);
      editor.off('selectionUpdate', tick);
    };
  }, [editor]);

  const [widthDraft, setWidthDraft] = React.useState('');
  const [propsOpen, setPropsOpen] = React.useState(false);

  const inside = !!editor && inTable(editor.state);
  const disabled = !editor || !canEdit;
  const gated = disabled || !inside;
  const reason = (base: string) => (!inside ? NOT_IN_TABLE : base);

  const run = (fn: () => boolean | null | void) => () => {
    if (!editor) return;
    fn();
    editor.commands.focus();
  };

  const table = editor && inside ? tableAt(editor) : null;
  const style = editor ? tableStyleAt(editor.state) : {};
  const penId = useBorderPen();
  const pen = borderPen(penId);
  // Word's rule, said out loud on every Apply button: a cell selection gets
  // the line; a bare caret gets the whole table's own lines.
  const borderScope = !inside
    ? NOT_IN_TABLE
    : editor && hasCellSelection(editor.state)
      ? 'Selected cells'
      : 'Whole table';
  const align = editor ? selectedCellAlign(editor.state) : null;
  const valign = editor ? selectedCellVAlign(editor.state) : null;
  const headerOn = !!editor?.isActive('tableHeader');
  const numbered = !!table?.numbered;
  const justify = style.tableJustify || 'left';
  const widthPct = style.width ?? MAX_TABLE_WIDTH_PCT;

  const commitWidth = () => {
    if (!editor || !widthDraft) return;
    const n = Number(widthDraft);
    if (Number.isFinite(n)) setTableWidth(editor, n);
    setWidthDraft('');
  };

  const toggleNumbered = run(() => {
    if (!editor) return;
    const at = table?.pos;
    if (at == null) return;
    editor.view.dispatch(editor.state.tr.setNodeAttribute(at, 'numbered', !numbered));
  });

  return (
    <div className="dsg-pal dsg-tbx sf-palette-flow">
      {/* ── Insert ── */}
      <div className="dsg-tbx-group" role="group" aria-label="Insert">
        <h3 className="dsg-tbx-head">Insert</h3>
        <InsertTableGridPicker
          disabled={disabled}
          onInsert={(rows, cols) =>
            editor &&
            insertTableBlock(editor.state, editor.view.dispatch.bind(editor.view), { rows, cols })
          }
        />
      </div>

      {/* ── Rows & columns ── */}
      <div className="dsg-tbx-group" role="group" aria-label="Rows and columns">
        <h3 className="dsg-tbx-head">Rows &amp; columns</h3>
        <div className="dsg-tbx-row">
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.addRowBefore())}
            aria-label="Insert row above"
            title={reason('Insert row above')}
          >
            <Icon icon={ArrowUpToLine} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.addRowAfter())}
            aria-label="Insert row below"
            title={reason('Insert row below')}
          >
            <Icon icon={ArrowDownToLine} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.deleteRow())}
            aria-label="Delete row"
            title={reason('Delete row')}
          >
            <Icon icon={Rows3} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.addColumnBefore())}
            aria-label="Insert column left"
            title={reason('Insert column left')}
          >
            <Icon icon={ArrowLeftToLine} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.addColumnAfter())}
            aria-label="Insert column right"
            title={reason('Insert column right')}
          >
            <Icon icon={ArrowRightToLine} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.deleteColumn())}
            aria-label="Delete column"
            title={reason('Delete column')}
          >
            <Icon icon={Columns3} em={1.1538} aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn"
          disabled={gated}
          onClick={run(() => editor && distributeColumns(editor))}
          title={reason('Give every column the same share of the table')}
        >
          <Icon
            icon={AlignHorizontalDistributeCenter}
            em={1.0769}
            aria-hidden="true"
            className="dsg-tbx-btn-icon"
          />
          Distribute columns evenly
        </button>
        <button
          type="button"
          className={`dsg-btn dsg-tbx-btn${style.autofit ? ' dsg-tool-on' : ''}`}
          disabled={gated}
          onClick={run(() => editor && autofitColumns(editor))}
          aria-pressed={!!style.autofit}
          title={reason('Let every column size itself to what is in it')}
        >
          <Icon icon={Expand} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Autofit contents
        </button>
      </div>

      {/* ── Cells ── */}
      <div className="dsg-tbx-group" role="group" aria-label="Cells">
        <h3 className="dsg-tbx-head">Cells</h3>
        <div className="dsg-tbx-row">
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.mergeCells())}
            aria-label="Merge selected cells"
            title={reason('Merge selected cells — drag across cells first')}
          >
            <Icon icon={TableCellsMerge} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.splitCell())}
            aria-label="Split cell"
            title={reason('Split a merged cell back apart')}
          >
            <Icon icon={TableCellsSplit} em={1.1538} aria-hidden="true" />
          </button>
        </div>
        <div className="dsg-tbx-row" role="group" aria-label="Cell text alignment">
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.setCellAttribute('align', 'left'))}
            aria-pressed={(align || 'left') === 'left'}
            aria-label="Align cell text left"
            title={reason('Align cell text left')}
          >
            <Icon icon={AlignLeft} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.setCellAttribute('align', 'center'))}
            aria-pressed={align === 'center'}
            aria-label="Align cell text center"
            title={reason('Align cell text center')}
          >
            <Icon icon={AlignCenter} em={1.1538} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dsg-icon-btn"
            disabled={gated}
            onClick={run(() => editor?.commands.setCellAttribute('align', 'right'))}
            aria-pressed={align === 'right'}
            aria-label="Align cell text right"
            title={reason('Align cell text right')}
          >
            <Icon icon={AlignRight} em={1.1538} aria-hidden="true" />
          </button>
        </div>
        <div className="dsg-tbx-row" role="group" aria-label="Cell vertical alignment">
          {VALIGNS.map((v) => (
            <button
              key={v.value}
              type="button"
              className="dsg-icon-btn"
              disabled={gated}
              onClick={run(() => editor && setCellVAlign(editor, v.value))}
              aria-pressed={(valign || 'top') === v.value}
              aria-label={v.label}
              title={reason(v.label)}
            >
              <Icon icon={v.icon} em={1.1538} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className="dsg-tbx-row">
          {/* ── Cell shading ── */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="dsg-icon-btn"
                disabled={gated}
                aria-label="Cell shading"
                title={reason('Cell shading')}
              >
                <Icon icon={PaintBucket} em={1.1538} aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="max-h-[var(--radix-popover-content-available-height)] min-w-[var(--dsg-tbl-pop-w)] overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg"
            >
              <div className="dsg-tbl-pop-label">Cell shading</div>
              {editor && (
                <ShadePicker
                  editor={editor}
                  disabled={gated}
                  itemRole="radio"
                  afterApply={() => editor.commands.focus()}
                />
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Borders (T1.3) — Word's Borders controls: pick a line (the pen),
             then where it goes. Replaces the old per-cell on/off toggles; the
             same capability is "pen + side" or No Border now. ── */}
      <div className="dsg-tbx-group" role="group" aria-label="Borders">
        <h3 className="dsg-tbx-head">Borders</h3>
        <div className="dsg-tbx-sub" id="dsg-bdr-line-label">
          Line
        </div>
        <div className="dsg-tbx-pens" role="radiogroup" aria-labelledby="dsg-bdr-line-label">
          {BORDER_PENS.map((p) => {
            const on = p.id === penId;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`dsg-tbx-pen${on ? ' dsg-tool-on' : ''}`}
                disabled={gated}
                onClick={() => setBorderPen(p.id)}
                title={reason(`${p.label} line`)}
              >
                {p.id === 'none' ? (
                  <Icon icon={Ban} em={1} aria-hidden="true" />
                ) : (
                  <span className={`dsg-pen-glyph is-${p.id}`} aria-hidden="true" />
                )}
                <span className="dsg-tbx-pen-label">{p.label}</span>
              </button>
            );
          })}
        </div>
        <div className="dsg-tbx-sub" id="dsg-bdr-apply-label">
          Apply
        </div>
        {/* Word's own grouping: the six single lines, then the four sets. */}
        <div className="dsg-tbx-row" role="group" aria-labelledby="dsg-bdr-apply-label">
          {BORDER_TARGETS.slice(0, 6).map((t) => (
            <button
              key={t.id}
              type="button"
              className="dsg-icon-btn"
              disabled={gated}
              onClick={run(() => editor && applyBorder(editor, t.id, pen.line))}
              aria-label={t.label}
              title={`${t.label}${t.id === 'none' ? '' : ` — ${pen.label}`} · ${borderScope}`}
            >
              <span className={TARGET_GLYPH[t.id]} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className="dsg-tbx-row" role="group" aria-labelledby="dsg-bdr-apply-label">
          {BORDER_TARGETS.slice(6).map((t) => (
            <button
              key={t.id}
              type="button"
              className="dsg-icon-btn"
              disabled={gated}
              onClick={run(() => editor && applyBorder(editor, t.id, pen.line))}
              aria-label={t.label}
              title={`${t.label}${t.id === 'none' ? '' : ` — ${pen.label}`} · ${borderScope}`}
            >
              <span className={TARGET_GLYPH[t.id]} aria-hidden="true" />
            </button>
          ))}
        </div>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn"
          disabled={gated}
          onClick={run(() => editor && setAllCellBorders(editor, null))}
          title={reason(
            'Clear the selected cells’ own borders — back to the lines the table draws'
          )}
        >
          Use the table&apos;s lines
        </button>
      </div>

      {/* ── Table ── */}
      <div className="dsg-tbx-group" role="group" aria-label="Table">
        <h3 className="dsg-tbx-head">Table</h3>
        <button
          type="button"
          className={`dsg-btn dsg-tbx-btn${headerOn ? ' dsg-tool-on' : ''}`}
          disabled={gated}
          onClick={run(() => editor?.commands.toggleHeaderRow())}
          aria-pressed={headerOn}
          title={reason('Header rows repeat on each page in Word')}
        >
          <Icon icon={PanelTop} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Header row
        </button>
        <button
          type="button"
          className={`dsg-btn dsg-tbx-btn${numbered ? ' dsg-tool-on' : ''}`}
          disabled={gated}
          onClick={toggleNumbered}
          aria-pressed={numbered}
          title={reason(
            numbered
              ? 'Numbered under this level — click to make it standalone'
              : 'Standalone (no number) — click to number it under this level'
          )}
        >
          <Icon icon={ListOrdered} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Numbered under level
        </button>
        <div className="dsg-tbx-row" role="group" aria-label="Table alignment">
          {JUSTIFIES.map((j) => (
            <button
              key={j.value}
              type="button"
              className="dsg-icon-btn"
              disabled={gated}
              onClick={run(() => editor && setTableJustify(editor, j.value))}
              aria-pressed={justify === j.value}
              aria-label={j.label}
              title={reason(j.label)}
            >
              <Icon icon={j.icon} em={1.1538} aria-hidden="true" />
            </button>
          ))}
        </div>
        <div className="dsg-tbx-num">
          <input
            type="number"
            className="dsg-menu-num-field"
            aria-label="Table width as a percentage of the page"
            min={MIN_TABLE_WIDTH_PCT}
            max={MAX_TABLE_WIDTH_PCT}
            value={widthDraft || widthPct}
            disabled={gated}
            title={reason('Table width')}
            onChange={(e) => setWidthDraft(e.target.value)}
            onBlur={commitWidth}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitWidth();
              }
            }}
          />
          <span className="dsg-menu-num-unit" aria-hidden="true">
            % width
          </span>
        </div>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn"
          disabled={gated}
          onClick={run(() => editor && splitTableAtSelection(editor))}
          title={reason('Break this table in two — the row you are in starts the second table')}
        >
          <Icon icon={Scissors} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Split table
        </button>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn"
          disabled={gated}
          onClick={() => setPropsOpen(true)}
          title={reason(
            'Width, alignment, padding, row height, header row and borders in one place'
          )}
        >
          <Icon icon={Settings2} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Table properties…
        </button>
        <button
          type="button"
          className="dsg-btn dsg-tbx-btn is-danger"
          disabled={gated}
          onClick={run(() => editor?.commands.deleteTable())}
          title={reason('Delete table — Ctrl+Z brings it back')}
        >
          <Icon icon={Trash2} em={1.0769} aria-hidden="true" className="dsg-tbx-btn-icon" />
          Delete table
        </button>
      </div>

      {editor && (
        <TablePropertiesDialog
          editor={editor}
          open={propsOpen}
          onOpenChange={setPropsOpen}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

/** Table properties… — every table-level and current-row setting in one
 *  dialog, for the person who wants them all at once instead of hunting
 *  through the toolbox groups. Every field applies through the SAME
 *  commands the toolbox buttons use; nothing here re-implements a write. */
function TablePropertiesDialog({
  editor,
  open,
  onOpenChange,
  canEdit,
}: {
  editor: Editor;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
}) {
  const style = tableStyleAt(editor.state);
  const rowHeight = selectedRowMinHeight(editor.state);
  const headerOn = editor.isActive('tableHeader');
  const justify = style.tableJustify || 'left';

  const [width, setWidth] = React.useState(String(style.width ?? MAX_TABLE_WIDTH_PCT));
  const [padding, setPadding] = React.useState(
    style.cellPadding !== undefined ? String(style.cellPadding) : ''
  );
  const [height, setHeight] = React.useState(rowHeight !== null ? String(rowHeight) : '');

  React.useEffect(() => {
    if (!open) return;
    setWidth(String(style.width ?? MAX_TABLE_WIDTH_PCT));
    setPadding(style.cellPadding !== undefined ? String(style.cellPadding) : '');
    setHeight(rowHeight !== null ? String(rowHeight) : '');
    // Snapshot the current values only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = () => {
    const w = Number(width);
    if (Number.isFinite(w)) setTableWidth(editor, w);
    const p = padding === '' ? null : Number(padding);
    setCellPadding(editor, p === null || Number.isFinite(p) ? p : null);
    const h = height === '' ? null : Number(height);
    setRowMinHeight(editor, h === null || Number.isFinite(h) ? h : null);
    editor.commands.focus();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Table properties</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="tbx-props-width">Width (% of page)</Label>
            <Input
              id="tbx-props-width"
              type="number"
              min={MIN_TABLE_WIDTH_PCT}
              max={MAX_TABLE_WIDTH_PCT}
              value={width}
              disabled={!canEdit}
              onChange={(e) => setWidth(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Alignment</Label>
            <div className="dsg-tbx-row mt-1" role="group" aria-label="Table alignment">
              {JUSTIFIES.map((j) => (
                <button
                  key={j.value}
                  type="button"
                  className="dsg-icon-btn"
                  disabled={!canEdit}
                  onClick={() => {
                    setTableJustify(editor, j.value);
                    editor.commands.focus();
                  }}
                  aria-pressed={justify === j.value}
                  aria-label={j.label}
                  title={j.label}
                >
                  <Icon icon={j.icon} em={1.1538} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="tbx-props-padding">Cell padding (pt)</Label>
            <Input
              id="tbx-props-padding"
              type="number"
              min={0}
              max={MAX_CELL_PADDING_PT}
              placeholder="Word default"
              value={padding}
              disabled={!canEdit}
              onChange={(e) => setPadding(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="tbx-props-height">Row height (pt) — selected row(s)</Label>
            <Input
              id="tbx-props-height"
              type="number"
              min={1}
              max={720}
              placeholder="Fits content"
              value={height}
              disabled={!canEdit}
              onChange={(e) => setHeight(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="tbx-props-header">Header row repeats on every page</Label>
            <Switch
              id="tbx-props-header"
              checked={headerOn}
              disabled={!canEdit}
              onCheckedChange={() => {
                editor.chain().focus().toggleHeaderRow().run();
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={apply} disabled={!canEdit}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
